import { Controller } from '@hotwired/stimulus';
import {csrfHeaders} from '../services/form.services';
import { updateManualAnalysisProgress } from '../services/manual-analysis-progress';

const MANUAL_ANALYSIS_JOB_KEY = 'ovasai.manualAnalysisJobId';

export class ManualEntryController extends Controller {
    declare readonly itemsContainerTarget: HTMLElement;
    declare readonly rowTemplateTarget: HTMLTemplateElement;

    static targets = ['itemsContainer', 'rowTemplate'];

    private mediaRecorder: MediaRecorder | null = null;
    private audioStream: MediaStream | null = null;
    private audioChunks: Blob[] = [];
    private isRecording = false;
    private isTranscribing = false;
    private speechToTextSupported = true;
    private activeRecordButton: HTMLButtonElement | null = null;
    private activeStatusElement: HTMLElement | null = null;
    private activeInput: HTMLTextAreaElement | null = null;
    private submitWatchdog: number | null = null;
    private activeSubmitter: HTMLButtonElement | null = null;
    private statusPollTimer: number | null = null;
    private connected = false;

    connect(): void {
        this.connected = true;
        this.speechToTextSupported = this.isSpeechToTextSupported();
        this.resumeManualAnalysis();
    }

    disconnect(): void {
        this.connected = false;
        this.stopSpeechCapture();
        this.clearSubmitWatchdog();
        this.clearStatusPollTimer();
    }

    cancelPendingAnalysis = (): void => {
        window.sessionStorage.removeItem(MANUAL_ANALYSIS_JOB_KEY);
        this.clearStatusPollTimer();
    };

    addRow(event?: Event): void {
        event?.preventDefault();

        const fragment = this.rowTemplateTarget.content.cloneNode(true);
        this.itemsContainerTarget.appendChild(fragment);
    }

    removeRow(event: Event): void {
        event.preventDefault();

        const trigger = event.currentTarget as HTMLElement | null;
        const row = trigger?.closest('[data-manual-entry-row]') as HTMLElement | null;
        if (!row) {
            return;
        }

        const rows = this.itemsContainerTarget.querySelectorAll('[data-manual-entry-row]');
        if (rows.length <= 1) {
            for (const input of row.querySelectorAll('input')) {
                if (input instanceof HTMLInputElement) {
                    input.value = input.type === 'number' ? (input.name === 'qty' ? '1' : '100') : '';
                }
            }

            for (const select of row.querySelectorAll('select')) {
                if (select instanceof HTMLSelectElement) {
                    select.selectedIndex = 0;
                }
            }
            return;
        }

        row.remove();
    }

    cancelEntry(event?: Event): void {
        event?.preventDefault();
        this.stopSpeechCapture();
        this.isTranscribing = false;

        const form = document.getElementById('inline-manual-analysis-form') as HTMLFormElement | null;
        form?.reset();
        document.querySelector<HTMLTextAreaElement>('#inline-manual-meal-description')?.blur();
        const status = document.querySelector<HTMLElement>('#inline-manual-meal-description-status');
        if (status) status.textContent = '';

        this.activeRecordButton = null;
        this.activeStatusElement = null;
        this.activeInput = null;
    }

    submitForm(event: Event): void {
        const form = event.currentTarget as HTMLFormElement | null;
        if (!form) {
            return;
        }

        const submitter = (event as SubmitEvent).submitter as HTMLButtonElement | null
            ?? form.querySelector('button[type="submit"]');

        if (!submitter) {
            return;
        }

        const isDescriptionAnalysis = form.action.endsWith('/analyse/manual/description');
        if (isDescriptionAnalysis) {
            event.preventDefault();
            const description = form.elements.namedItem('meal_description') as HTMLTextAreaElement | null;
            const audio = form.elements.namedItem('audio_base64') as HTMLInputElement | null;
            if (!description?.value.trim() && !audio?.value) {
                this.resetSubmitState(
                    submitter,
                    'Add a short meal description or record your meal first.'
                );
                return;
            }
        }

        if (form.dataset.manualEntrySubmitting === 'true') {
            event.preventDefault();
            return;
        }

        form.dataset.manualEntrySubmitting = 'true';
        this.clearSubmitWatchdog();
        this.activeSubmitter = submitter;
        submitter.dataset.manualEntryOriginalText = submitter.textContent?.trim() || 'Estimate This Meal';
        submitter.setAttribute('aria-disabled', 'true');
        submitter.classList.add('cursor-wait', 'opacity-80', 'pointer-events-none');

        const spinner = submitter.querySelector('[data-manual-entry-spinner]');
        const label = submitter.querySelector('[data-manual-entry-label]');
        const loading = submitter.querySelector('[data-manual-entry-loading]');

        if (label instanceof HTMLElement) {
            label.classList.add('hidden');
        }

        if (spinner instanceof HTMLElement && loading instanceof HTMLElement) {
            loading.classList.remove('hidden');
            loading.classList.add('inline-flex');
            loading.style.display = 'inline-flex';
        } else if (spinner instanceof HTMLElement) {
            spinner.classList.remove('hidden');
        }

        this.submitWatchdog = window.setTimeout(() => {
            this.resetSubmitState(
                submitter,
                'This is taking longer than expected. Please check your connection and try again.'
            );
        }, 75000);

        if (isDescriptionAnalysis) {
            updateManualAnalysisProgress(this.element, 'queued');
            this.setSubmitStatus(submitter, '');
            const description = form.elements.namedItem('meal_description') as HTMLTextAreaElement | null;
            if (this.element.matches('[data-controller~="queued-meal"]')) {
                this.element.dispatchEvent(new CustomEvent('manual-entry:queue', {
                    bubbles: true,
                    detail: { description: description?.value.trim() || '' },
                }));
            } else {
                void this.queueManualAnalysis(form, submitter);
            }
        }
    }

    manualQueued(): void {
        if (this.activeSubmitter) this.resetSubmitState(this.activeSubmitter, '');
        this.cancelEntry();
    }

    manualQueueFailed(event: CustomEvent<{ message?: string }>): void {
        if (!this.activeSubmitter) return;
        this.resetSubmitState(
            this.activeSubmitter,
            event.detail?.message || 'The manual item could not be queued.'
        );
    }

    toggleRecording = async (event: Event): Promise<void> => {
        event.preventDefault();

        const trigger = event.currentTarget as HTMLButtonElement | null;
        if (!trigger) {
            return;
        }

        const inputSelector = trigger.dataset.manualEntryInputSelector;
        const statusSelector = trigger.dataset.manualEntryStatusSelector;
        const input = inputSelector ? document.querySelector(inputSelector) as HTMLTextAreaElement | null : null;
        const status = statusSelector ? document.querySelector(statusSelector) as HTMLElement | null : null;

        if (!input) {
            return;
        }

        if (this.isTranscribing) {
            return;
        }

        if (this.isRecording && trigger === this.activeRecordButton) {
            this.mediaRecorder?.stop();
            return;
        }

        if (!this.speechToTextSupported) {
            this.setRecordStatus(status, 'Mic unavailable in this browser.');
            return;
        }

        this.activeRecordButton = trigger;
        this.activeStatusElement = status;
        this.activeInput = input;

        try {
            this.setRecordStatus(status, 'Requesting microphone...');
            this.audioChunks = [];
            this.audioStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            this.mediaRecorder = new MediaRecorder(this.audioStream);

            this.mediaRecorder.addEventListener('dataavailable', (mediaEvent: BlobEvent) => {
                if (mediaEvent.data.size > 0) {
                    this.audioChunks.push(mediaEvent.data);
                }
            });

            this.mediaRecorder.addEventListener('stop', async () => {
                const mimeType = this.mediaRecorder?.mimeType || this.audioChunks[0]?.type || 'audio/webm';
                const audioBlob = new Blob(this.audioChunks, { type: mimeType });
                this.stopSpeechCapture();
                await this.transcribeAudio(audioBlob);
            });

            this.mediaRecorder.start();
            this.isRecording = true;
            this.updateSpeechToTextUi();
        } catch {
            this.stopSpeechCapture();
            this.setRecordStatus(status, 'Microphone blocked.');
        }
    }

    private async transcribeAudio(audioBlob: Blob): Promise<void> {
        if (audioBlob.size === 0) {
            this.setRecordStatus(this.activeStatusElement, 'No speech captured.');
            return;
        }

        this.isTranscribing = true;
        this.updateSpeechToTextUi();

        try {
            const extension = this.mimeTypeToFileExtension(audioBlob.type);
            const audioFile = new File([audioBlob], `manual-meal.${extension}`, { type: audioBlob.type || 'audio/webm' });
            const associatedFormId = this.activeInput?.getAttribute('form');
            const form = associatedFormId
                ? document.getElementById(associatedFormId) as HTMLFormElement | null
                : this.activeInput?.closest('form');
            if (form?.action.endsWith('/analyse/manual/description')
                && !this.element.matches('[data-controller~="queued-meal"]')) {
                const encodedAudio = await this.blobToBase64(audioBlob);
                const audioInput = form.elements.namedItem('audio_base64') as HTMLInputElement | null;
                const filenameInput = form.elements.namedItem('audio_filename') as HTMLInputElement | null;
                const contentTypeInput = form.elements.namedItem('audio_content_type') as HTMLInputElement | null;
                if (!audioInput || !filenameInput || !contentTypeInput) {
                    throw new Error('Voice recording could not be prepared.');
                }
                audioInput.value = encodedAudio;
                filenameInput.value = audioFile.name;
                contentTypeInput.value = audioFile.type;
                this.setRecordStatus(
                    this.activeStatusElement,
                    'Recording ready. Submit when you are ready.'
                );
                return;
            }

            const formData = new FormData();
            formData.append('audio', audioFile);

            const response = await fetch('/analyse/transcribe-details', {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    ...csrfHeaders(),
                },
                body: formData,
            });

            const data = await response.json().catch(() => null);
            if (!response.ok || !data?.success || typeof data.transcript !== 'string') {
                throw new Error(data?.message || 'Speech transcription failed.');
            }

            this.appendTranscript(data.transcript);
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Speech transcription failed.';
            this.setRecordStatus(this.activeStatusElement, message);
        } finally {
            this.isTranscribing = false;
            this.updateSpeechToTextUi();
        }
    }

    private async queueManualAnalysis(
        form: HTMLFormElement,
        submitter: HTMLButtonElement
    ): Promise<void> {
        try {
            const response = await fetch(form.action, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    ...csrfHeaders(),
                },
                body: new FormData(form),
            });
            const payload = await response.json().catch(() => null) as {
                jobId?: number;
                message?: string;
            } | null;
            if (!response.ok || !payload?.jobId) {
                throw new Error(payload?.message || 'The analysis could not be started.');
            }

            window.sessionStorage.setItem(MANUAL_ANALYSIS_JOB_KEY, String(payload.jobId));
            this.clearSubmitWatchdog();
            this.setSubmitStatus(submitter, '');
            updateManualAnalysisProgress(this.element, 'queued');
            this.scheduleManualAnalysisPoll(500);
        } catch (error) {
            const message = error instanceof Error
                ? error.message
                : 'The analysis could not be started.';
            this.resetSubmitState(submitter, message);
        }
    }

    private resumeManualAnalysis(): void {
        const jobIdFromUrl = new URLSearchParams(window.location.search).get('manual_job_id');
        if (jobIdFromUrl && /^\d+$/.test(jobIdFromUrl)) {
            window.sessionStorage.setItem(MANUAL_ANALYSIS_JOB_KEY, jobIdFromUrl);
        }
        if (window.sessionStorage.getItem(MANUAL_ANALYSIS_JOB_KEY)) {
            updateManualAnalysisProgress(this.element, 'queued', 'Checking your meal analysis…');
            this.scheduleManualAnalysisPoll(250);
        }
    }

    private scheduleManualAnalysisPoll(delayMs = 3000): void {
        this.clearStatusPollTimer();
        this.statusPollTimer = window.setTimeout(() => {
            void this.pollManualAnalysis();
        }, delayMs);
    }

    private async pollManualAnalysis(): Promise<void> {
        const jobId = window.sessionStorage.getItem(MANUAL_ANALYSIS_JOB_KEY);
        if (!jobId || !this.connected) {
            return;
        }

        try {
            const response = await fetch(
                `/analyse/status?job_id=${encodeURIComponent(jobId)}`,
                {
                    headers: {
                        Accept: 'application/json',
                        ...csrfHeaders(),
                    },
                }
            );
            const payload = await response.json().catch(() => null) as {
                status?: string;
                redirectUrl?: string;
                message?: string;
            } | null;
            if (!this.connected || window.sessionStorage.getItem(MANUAL_ANALYSIS_JOB_KEY) !== jobId) return;
            if (!response.ok || !payload) {
                this.scheduleManualAnalysisPoll();
                return;
            }
            if (payload.redirectUrl) {
                updateManualAnalysisProgress(this.element, 'ready', payload.message);
                window.sessionStorage.removeItem(MANUAL_ANALYSIS_JOB_KEY);
                window.location.assign(payload.redirectUrl);
                return;
            }
            if (payload.status === 'failed') {
                updateManualAnalysisProgress(this.element, 'failed', payload.message);
                window.sessionStorage.removeItem(MANUAL_ANALYSIS_JOB_KEY);
                if (this.activeSubmitter) {
                    this.resetSubmitState(
                        this.activeSubmitter,
                        payload.message || 'The analysis could not be completed.'
                    );
                }
                return;
            }
            if (payload.status === 'idle') {
                updateManualAnalysisProgress(this.element, 'idle', 'The analysis could not be found. Please try again.');
                window.sessionStorage.removeItem(MANUAL_ANALYSIS_JOB_KEY);
                if (this.activeSubmitter) {
                    this.resetSubmitState(
                        this.activeSubmitter,
                        'The background analysis could not be found. Please try again.'
                    );
                }
                return;
            }
            if (payload.status === 'superseded') {
                updateManualAnalysisProgress(this.element, 'superseded', payload.message);
                window.sessionStorage.removeItem(MANUAL_ANALYSIS_JOB_KEY);
                if (this.activeSubmitter) {
                    this.resetSubmitState(
                        this.activeSubmitter,
                        payload.message || 'A newer analysis has replaced this one.'
                    );
                }
                return;
            }
            if (payload.status) updateManualAnalysisProgress(this.element, payload.status, payload.message);
        } catch {
            // A transient connection failure should not discard a durable job.
        }
        if (this.connected && window.sessionStorage.getItem(MANUAL_ANALYSIS_JOB_KEY) === jobId) {
            this.scheduleManualAnalysisPoll();
        }
    }

    private clearStatusPollTimer(): void {
        if (this.statusPollTimer !== null) {
            window.clearTimeout(this.statusPollTimer);
            this.statusPollTimer = null;
        }
    }

    private blobToBase64(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.addEventListener('load', () => {
                const value = typeof reader.result === 'string' ? reader.result : '';
                const encoded = value.split(',', 2)[1] || '';
                if (!encoded) {
                    reject(new Error('Voice recording could not be encoded.'));
                    return;
                }
                resolve(encoded);
            });
            reader.addEventListener('error', () => reject(reader.error));
            reader.readAsDataURL(blob);
        });
    }

    private appendTranscript(transcript: string): void {
        const input = this.activeInput;
        if (!input) {
            return;
        }

        const safeTranscript = transcript.trim();
        if (!safeTranscript) {
            return;
        }

        const existingText = input.value.trim();
        input.value = existingText ? `${existingText} ${safeTranscript}` : safeTranscript;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        this.setRecordStatus(this.activeStatusElement, 'Details added. Review and edit if needed.');
        input.focus();
    }

    private stopSpeechCapture(): void {
        if (this.mediaRecorder) {
            this.mediaRecorder.ondataavailable = null;
            this.mediaRecorder.onstop = null;
            this.mediaRecorder = null;
        }

        if (this.audioStream) {
            this.audioStream.getTracks().forEach(track => track.stop());
            this.audioStream = null;
        }

        this.isRecording = false;
        this.audioChunks = [];
        this.updateSpeechToTextUi();
    }

    private updateSpeechToTextUi(): void {
        const button = this.activeRecordButton;
        if (!button) {
            return;
        }

        button.disabled = this.isTranscribing;
        button.classList.toggle('opacity-60', this.isTranscribing);
        button.classList.toggle('cursor-not-allowed', this.isTranscribing);
        button.classList.remove('text-red-600', 'text-zinc-600');
        button.classList.add('text-zinc-500');

        if (!this.speechToTextSupported) {
            this.setRecordIcon(button, 'mic');
            return;
        }

        if (this.isTranscribing) {
            button.classList.remove('text-zinc-500');
            button.classList.add('text-zinc-600');
            this.setRecordIcon(button, 'spinner');
            this.setRecordStatus(this.activeStatusElement, 'Transcribing...');
            return;
        }

        if (this.isRecording) {
            button.classList.remove('text-zinc-500');
            button.classList.add('text-red-600');
            this.setRecordIcon(button, 'stop');
            this.setRecordStatus(this.activeStatusElement, 'Recording...');
            return;
        }

        this.setRecordIcon(button, 'mic');
    }

    private setRecordStatus(element: HTMLElement | null, message: string): void {
        if (element) {
            element.textContent = message;
        }
    }

    private clearSubmitWatchdog(): void {
        if (this.submitWatchdog !== null) {
            window.clearTimeout(this.submitWatchdog);
            this.submitWatchdog = null;
        }
    }

    private resetSubmitState(submitter: HTMLButtonElement, message: string): void {
        if (message && submitter.form?.action.endsWith('/analyse/manual/description')) {
            updateManualAnalysisProgress(this.element, 'failed', message);
        }
        this.clearSubmitWatchdog();
        submitter.disabled = false;
        submitter.removeAttribute('aria-disabled');
        submitter.classList.remove('cursor-wait', 'opacity-80', 'pointer-events-none');
        const form = submitter.form;
        if (form instanceof HTMLFormElement) {
            delete form.dataset.manualEntrySubmitting;
        }

        const label = submitter.querySelector('[data-manual-entry-label]');
        const loading = submitter.querySelector('[data-manual-entry-loading]');
        const spinner = submitter.querySelector('[data-manual-entry-spinner]');

        if (label instanceof HTMLElement) {
            label.classList.remove('hidden');
        }

        if (loading instanceof HTMLElement) {
            loading.classList.add('hidden');
            loading.classList.remove('inline-flex');
            loading.style.display = '';
        }

        if (spinner instanceof HTMLElement) {
            spinner.classList.add('hidden');
        }

        this.setSubmitStatus(submitter, message);
        this.activeSubmitter = null;
    }

    private setSubmitStatus(submitter: HTMLButtonElement, message: string): void {
        const form = submitter.form;
        if (!form) {
            return;
        }

        let status = form.querySelector('[data-manual-entry-submit-status]') as HTMLElement | null;
        if (!status) {
            status = document.createElement('p');
            status.dataset.manualEntrySubmitStatus = 'true';
            status.className = 'manual-entry-submit-status text-sm text-red-600';
            submitter.insertAdjacentElement('afterend', status);
        }
        status.textContent = message;
    }

    private isSpeechToTextSupported(): boolean {
        return Boolean(window.MediaRecorder && navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    }

    private mimeTypeToFileExtension(mimeType: string): string {
        const normalizedType = mimeType.split(';', 1)[0].trim().toLowerCase();
        switch (normalizedType) {
            case 'audio/mp4':
            case 'audio/x-m4a':
            case 'audio/m4a':
                return 'm4a';
            case 'audio/mpeg':
                return 'mp3';
            case 'audio/wav':
            case 'audio/x-wav':
                return 'wav';
            case 'audio/ogg':
                return 'ogg';
            default:
                return 'webm';
        }
    }

    private setRecordIcon(button: HTMLButtonElement, icon: 'mic' | 'stop' | 'spinner'): void {
        if (icon === 'stop') {
            button.innerHTML = `
                <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <rect x="4" y="4" width="12" height="12" rx="1.5"></rect>
                </svg>
            `;
            return;
        }

        if (icon === 'spinner') {
            button.innerHTML = `
                <svg class="h-5 w-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            `;
            return;
        }

        button.innerHTML = `
            <svg class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M8.25 5.25a3.75 3.75 0 117.5 0v6.75a3.75 3.75 0 11-7.5 0V5.25z" />
                <path d="M6 11.25a.75.75 0 011.5 0 4.5 4.5 0 109 0 .75.75 0 011.5 0 6.001 6.001 0 01-5.25 5.955V19.5H15a.75.75 0 010 1.5H9a.75.75 0 010-1.5h2.25v-2.295A6.001 6.001 0 016 11.25z" />
            </svg>
        `;
    }
}
